import { useEffect, useMemo, useState, Fragment } from "react";
import { api, API, auth } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import MultiSelect from "@/components/MultiSelect";
import { Download, Filter, RefreshCw, ChevronDown, ChevronRight, ShieldCheck, ShieldAlert, ShieldOff, MinusCircle, Loader2, Users, Mail } from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  "Compliant":     { icon: ShieldCheck,  cls: "bg-emerald-100 text-emerald-700",   tone: "text-emerald-600" },
  "Partial":       { icon: ShieldAlert,  cls: "bg-amber-100 text-amber-700",       tone: "text-amber-600" },
  "Non-Compliant": { icon: ShieldOff,    cls: "bg-red-100 text-red-700",           tone: "text-red-600" },
  "No Assignments":{ icon: MinusCircle,  cls: "bg-slate-100 text-slate-500",       tone: "text-slate-400" },
};

/** Training Compliance Report — filters + expandable rows + xlsx export. */
export default function ComplianceReport() {
  const [filters, setFilters] = useState({
    date_from: "", date_to: "",
    department_id: [], category_id: [],
    reporting_manager_emp_id: "",
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");   // client-side pill filter
  const [openRow, setOpenRow] = useState(null);
  const [masters, setMasters] = useState({ depts: [], cats: [], managers: [] });
  const [sending, setSending] = useState(false);

  async function sendRemindersToVisible() {
    // If a status pill is active, send to those rows; else default to Non-Compliant + Partial.
    const targets = statusFilter
      ? filteredRows
      : rows.filter(r => r.status === "Non-Compliant" || r.status === "Partial");
    if (targets.length === 0) {
      toast.error("No employees to notify");
      return;
    }
    if (!window.confirm(`Send reminder email to ${targets.length} employee${targets.length > 1 ? "s" : ""}?`)) return;
    setSending(true);
    try {
      const emp_db_ids = targets.map(r => r.emp_db_id);
      const r = await api.post("/notifications/send-reminder", {
        emp_db_ids, cc_reporting_manager: true,
      });
      const d = r.data;
      const desc = [
        d.sent_count ? `${d.sent_count} sent` : "",
        d.mocked_count ? `${d.mocked_count} mocked (SMTP not configured)` : "",
        d.failed_count ? `${d.failed_count} failed` : "",
      ].filter(Boolean).join(" · ");
      if (!d.smtp_configured && d.mocked_count) {
        toast.warning("Email is running in MOCK mode. Set up SMTP in Integrations to send real emails.", { description: desc });
      } else {
        toast.success("Reminders processed", { description: desc });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  }

  useEffect(() => {
    (async () => {
      const [d, c, e] = await Promise.all([
        api.get("/masters/departments"),
        api.get("/masters/categories"),
        api.get("/employees", { params: { limit: 1000, page: 1 } }),
      ]);
      const emps = e.data.data || e.data || [];
      // Managers = distinct emp_ids referenced as `reporting_manager_emp_id` on any employee
      const managerIds = Array.from(new Set(emps.map(x => x.reporting_manager_emp_id).filter(Boolean)));
      const managers = emps.filter(x => managerIds.includes(x.emp_id));
      setMasters({ depts: d.data, cats: c.data, managers });
    })().catch(() => {});
  }, []);

  async function load() {
    setLoading(true);
    try {
      const params = buildParams(filters);
      const r = await api.get("/reports/compliance", { params });
      setData(r.data);
    } catch { toast.error("Failed to load compliance report"); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); /* auto-load on first mount */ }, []);

  async function exportXlsx() {
    const params = new URLSearchParams();
    Object.entries(buildParams(filters)).forEach(([k, v]) => v && params.set(k, v));
    try {
      const resp = await fetch(`${API}/reports/compliance/export?${params}`, {
        headers: { Authorization: `Bearer ${auth.token()}` },
      });
      if (!resp.ok) throw new Error("failed");
      const blob = await resp.blob();
      const fname = resp.headers.get("Content-Disposition")?.match(/filename="(.+?)"/)?.[1] || `compliance-${Date.now()}.xlsx`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = fname; a.click();
      URL.revokeObjectURL(url);
      toast.success("Compliance report downloaded");
    } catch { toast.error("Export failed"); }
  }

  const rows = data?.rows || [];
  const totals = data?.totals || { employees: 0, compliant: 0, partial: 0, non_compliant: 0, no_assignments: 0 };
  const filteredRows = useMemo(() => {
    if (!statusFilter) return rows;
    return rows.filter(r => r.status === statusFilter);
  }, [rows, statusFilter]);

  const managerOptions = useMemo(
    () => [{ value: "", label: "All reporting managers" }, ...masters.managers.map(m => ({ value: m.emp_id, label: `${m.name} (${m.emp_id})` }))],
    [masters.managers]
  );

  return (
    <div className="space-y-5" data-testid="compliance-report">
      {/* Filters */}
      <Card className="border-slate-200 rounded-2xl">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-sky-600"/>
            <div className="font-bold text-sm">Filters</div>
            <div className="ml-auto text-xs text-slate-400">Rows are filtered server-side. Status pills below filter locally.</div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">From</Label>
              <Input type="date" value={filters.date_from} onChange={e => setFilters({ ...filters, date_from: e.target.value })} data-testid="cmp-from"/>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">To</Label>
              <Input type="date" value={filters.date_to} onChange={e => setFilters({ ...filters, date_to: e.target.value })} data-testid="cmp-to"/>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Department</Label>
              <MultiSelect value={filters.department_id} onChange={v => setFilters({ ...filters, department_id: v })} options={masters.depts} testid="cmp-dept" placeholder="All departments"/>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Category</Label>
              <MultiSelect value={filters.category_id} onChange={v => setFilters({ ...filters, category_id: v })} options={masters.cats} testid="cmp-cat" placeholder="All categories"/>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Reporting Manager</Label>
              <Select value={filters.reporting_manager_emp_id || "__all__"} onValueChange={v => setFilters({ ...filters, reporting_manager_emp_id: v === "__all__" ? "" : v })}>
                <SelectTrigger data-testid="cmp-manager"><SelectValue placeholder="All"/></SelectTrigger>
                <SelectContent>
                  {managerOptions.map(o => <SelectItem key={o.value || "__all__"} value={o.value || "__all__"}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={load} disabled={loading} className="bg-sky-500 hover:bg-sky-600" data-testid="cmp-apply">
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1.5"/> : <RefreshCw className="h-4 w-4 mr-1.5"/>}Apply
            </Button>
            <Button variant="outline" onClick={() => { setFilters({ date_from: "", date_to: "", department_id: [], category_id: [], reporting_manager_emp_id: "" }); setTimeout(load, 0); }}>Clear</Button>
            <div className="ml-auto flex gap-2">
              <Button onClick={sendRemindersToVisible} disabled={sending || filteredRows.length === 0} variant="outline" data-testid="cmp-send-reminders">
                {sending ? <Loader2 className="h-4 w-4 animate-spin mr-1.5"/> : <Mail className="h-4 w-4 mr-1.5"/>}
                Send reminder to {statusFilter ? `these ${filteredRows.length}` : "non-compliant"}
              </Button>
              <Button onClick={exportXlsx} className="bg-emerald-600 hover:bg-emerald-700" data-testid="cmp-export">
                <Download className="h-4 w-4 mr-1.5"/>Export .xlsx (Summary + Detail)
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Status pills (client-side) */}
      <div className="flex flex-wrap gap-2">
        <StatusPill label="All Employees"    icon={Users}        n={totals.employees}       active={statusFilter === ""}               onClick={() => setStatusFilter("")}                tid="cmp-pill-all"/>
        <StatusPill label="Compliant"        icon={ShieldCheck}  n={totals.compliant}       active={statusFilter === "Compliant"}      onClick={() => setStatusFilter("Compliant")}      color="emerald" tid="cmp-pill-compliant"/>
        <StatusPill label="Partial"          icon={ShieldAlert}  n={totals.partial}         active={statusFilter === "Partial"}        onClick={() => setStatusFilter("Partial")}        color="amber"   tid="cmp-pill-partial"/>
        <StatusPill label="Non-Compliant"    icon={ShieldOff}    n={totals.non_compliant}   active={statusFilter === "Non-Compliant"}  onClick={() => setStatusFilter("Non-Compliant")}  color="red"     tid="cmp-pill-non"/>
        <StatusPill label="No Assignments"   icon={MinusCircle}  n={totals.no_assignments}  active={statusFilter === "No Assignments"} onClick={() => setStatusFilter("No Assignments")} color="slate"   tid="cmp-pill-none"/>
      </div>

      {/* Table */}
      <Card className="border-slate-200 rounded-2xl">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="cmp-table">
              <thead className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
                <tr>
                  <th className="w-6 py-3"></th>
                  <th className="py-3 px-4">Employee</th>
                  <th className="py-3 px-4">Department</th>
                  <th className="py-3 px-4">Category</th>
                  <th className="py-3 px-4">Manager</th>
                  <th className="py-3 px-4 text-center">Assigned</th>
                  <th className="py-3 px-4 text-center">Completed</th>
                  <th className="py-3 px-4 text-center">Pending</th>
                  <th className="py-3 px-4">Compliance</th>
                  <th className="py-3 px-4">Status</th>
                </tr>
              </thead>
              <tbody>
                {loading && !data && (
                  <tr><td colSpan="10" className="py-16 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin mx-auto"/></td></tr>
                )}
                {!loading && filteredRows.length === 0 && (
                  <tr><td colSpan="10" className="py-16 text-center text-slate-400">No employees match your filters.</td></tr>
                )}
                {filteredRows.map(r => {
                  const isOpen = openRow === r.emp_db_id;
                  const meta = STATUS_META[r.status] || STATUS_META["No Assignments"];
                  const Icon = meta.icon;
                  return (
                    <Fragment key={r.emp_db_id}>
                      <tr className={`border-b border-slate-100 hover:bg-slate-50/60 cursor-pointer ${isOpen ? "bg-sky-50/40" : ""}`} onClick={() => setOpenRow(isOpen ? null : r.emp_db_id)} data-testid={`cmp-row-${r.emp_db_id}`}>
                        <td className="pl-4 py-3">{isOpen ? <ChevronDown className="h-4 w-4 text-slate-400"/> : <ChevronRight className="h-4 w-4 text-slate-400"/>}</td>
                        <td className="py-3 px-4">
                          <div className="font-medium text-slate-900">{r.name}</div>
                          <div className="text-xs text-slate-500 font-mono">{r.emp_id}</div>
                        </td>
                        <td className="py-3 px-4 text-slate-700">{r.department_name}</td>
                        <td className="py-3 px-4 text-slate-700">{r.category_name}</td>
                        <td className="py-3 px-4 text-slate-600">
                          {r.reporting_manager ? (
                            <div>
                              <div>{r.reporting_manager}</div>
                              {r.reporting_manager_emp_id && <div className="text-xs text-slate-400 font-mono">{r.reporting_manager_emp_id}</div>}
                            </div>
                          ) : <span className="text-slate-300 text-xs">—</span>}
                        </td>
                        <td className="py-3 px-4 text-center font-medium">{r.assigned}</td>
                        <td className="py-3 px-4 text-center text-emerald-600 font-medium">{r.completed}</td>
                        <td className="py-3 px-4 text-center text-amber-600 font-medium">{r.pending}</td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <div className="w-24 h-2 rounded-full bg-slate-100 overflow-hidden">
                              <div className={`h-full ${r.compliance_pct >= 100 ? "bg-emerald-500" : r.compliance_pct >= 50 ? "bg-amber-500" : "bg-red-500"}`} style={{ width: `${Math.min(r.compliance_pct, 100)}%` }}/>
                            </div>
                            <span className="text-xs font-bold text-slate-700 min-w-[40px]">{r.compliance_pct}%</span>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <Badge className={meta.cls}><Icon className="h-3 w-3 mr-1"/>{r.status}</Badge>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="bg-sky-50/30 border-b border-slate-100" data-testid={`cmp-detail-${r.emp_db_id}`}>
                          <td/>
                          <td colSpan="9" className="p-4">
                            {r.trainings.length === 0 ? (
                              <div className="text-slate-500 text-sm px-2">No trainings assigned to this employee.</div>
                            ) : (
                              <div className="rounded-xl overflow-hidden border border-slate-200 bg-white">
                                <table className="w-full text-xs">
                                  <thead className="text-left text-[10px] uppercase tracking-wider text-slate-500 bg-slate-50">
                                    <tr>
                                      <th className="py-2 px-3">Quiz</th>
                                      <th className="py-2 px-3">Phase</th>
                                      <th className="py-2 px-3">Status</th>
                                      <th className="py-2 px-3">Score</th>
                                      <th className="py-2 px-3">Completed At</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {r.trainings.map((t, i) => (
                                      <tr key={i} className="border-b border-slate-100 last:border-0">
                                        <td className="py-2 px-3">
                                          <div className="font-medium text-slate-900">{t.quiz_title}</div>
                                          {t.topic && <div className="text-slate-500 text-[11px]">{t.topic}</div>}
                                        </td>
                                        <td className="py-2 px-3">{t.phase ? <Badge variant="secondary" className="bg-slate-100 text-slate-700 uppercase">{t.phase}</Badge> : <span className="text-slate-300">—</span>}</td>
                                        <td className="py-2 px-3">
                                          <Badge className={t.status === "Completed" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>{t.status}</Badge>
                                        </td>
                                        <td className="py-2 px-3">{t.score_pct !== null && t.score_pct !== undefined ? <span className="font-bold text-slate-900">{t.score_pct}%</span> : <span className="text-slate-300">—</span>}</td>
                                        <td className="py-2 px-3 text-slate-600">{t.completed_at ? new Date(t.completed_at).toLocaleDateString() : <span className="text-slate-300">—</span>}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function buildParams(filters) {
  const p = {};
  if (filters.date_from) p.date_from = filters.date_from;
  if (filters.date_to) p.date_to = filters.date_to;
  if (filters.department_id?.length) p.department_id = filters.department_id.join(",");
  if (filters.category_id?.length) p.category_id = filters.category_id.join(",");
  if (filters.reporting_manager_emp_id) p.reporting_manager_emp_id = filters.reporting_manager_emp_id;
  return p;
}

function StatusPill({ label, icon: Icon, n, active, onClick, color, tid }) {
  const activeCls = {
    "": "bg-slate-900 text-white",
    emerald: "bg-emerald-600 text-white",
    amber: "bg-amber-500 text-white",
    red: "bg-red-600 text-white",
    slate: "bg-slate-600 text-white",
  }[color || ""];
  return (
    <button onClick={onClick} data-testid={tid}
      className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border font-bold text-sm transition ${active ? activeCls + " border-transparent shadow" : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"}`}>
      <Icon className="h-4 w-4"/>
      {label}
      <span className={`inline-flex items-center justify-center rounded-full text-xs h-5 min-w-[20px] px-1.5 ${active ? "bg-white/20" : "bg-slate-100 text-slate-600"}`}>{n}</span>
    </button>
  );
}
